/* eslint-disable */
// ═══════════════════════════════════════════════════════════════════════════
// CATALOGUE — composants partagés réutilisables
//
// Utilisé par :
//   - src/pages/Catalogue.js  (page autonome /catalogue)
//   - src/pages/Admin.js      (onglet "Catalogue" dans Paramètres)
//
// Exporte :
//   - Tokens & helpers : D, card, api
//   - Composants UI : Modal, Field, Btn, Filters, SourceBadge, ActiveBadge, DataTable
//   - Constantes : STATUS_BUCKETS, TYPE_BUCKETS, TABS
//   - Tabs CRUD : TabStatuts, TabTypes, TabSubTypes, TabGreffes, TabCabinets
//   - Composant complet : CatalogueContent (pour usage embarqué dans Paramètres)
// ═══════════════════════════════════════════════════════════════════════════
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_API_URL || 'https://lexback-production.up.railway.app';
export const api = axios.create({ baseURL: API });
api.interceptors.request.use(c => {
  const t = localStorage.getItem('lexback_token');
  if (t) c.headers.Authorization = `Bearer ${t}`;
  return c;
});

// ─── Design tokens (identiques à Admin.js) ──────────────────────────────────
export const D = {
  bg:    '#13131A',
  s1:    '#1C1C28',
  s2:    '#252533',
  b1:    '#3A3A4A',
  b2:    'rgba(255,255,255,0.16)',
  tx:    '#EDEAE4',
  tx2:   '#B8B8C4',
  tx3:   '#7E7E8E',
  or:    '#D4A843',
  green: '#2ECC8A',
  red:   '#EF5B5B',
  blue:  '#5B8DEF',
  amber: '#F0A843',
};

export const card = { background: D.s1, border: `1px solid ${D.b1}`, borderRadius: '10px' };

export const STATUS_BUCKETS = ['recu','assigne','en_cours','depose','termine','rejete','doc_manquant'];
export const TYPE_BUCKETS = ['creation_sas','creation_sarl','creation_sasu','creation_eurl','creation_sci','creation_ei','modification','dissolution','radiation','transfert_siege','depot_comptes','autre'];

export const TABS = [
  { id: 'statuts',    label: 'Statuts' },
  { id: 'types',      label: 'Types de formalité' },
  { id: 'sous-types', label: 'Sous-types' },
  { id: 'greffes',    label: 'Greffes' },
  { id: 'cabinets',   label: 'Cabinets' },
];

// ═══════════════════════════════════════════════════════════════════════════
// SHARED UI COMPONENTS
// ═══════════════════════════════════════════════════════════════════════════

export function Modal({ title, onClose, children }) {
  return (
    <div style={{ position:'fixed', inset:0, zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', background:'rgba(0,0,0,0.6)' }} onClick={onClose}>
      <div style={{ ...card, padding:'24px', minWidth:'420px', maxWidth:'520px', maxHeight:'80vh', overflowY:'auto' }} onClick={e => e.stopPropagation()}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'16px' }}>
          <span style={{ fontFamily:'Syne, sans-serif', fontSize:'16px', fontWeight:700, color:D.or }}>{title}</span>
          <span onClick={onClose} style={{ cursor:'pointer', color:D.tx3, fontSize:'18px' }}>×</span>
        </div>
        {children}
      </div>
    </div>
  );
}

export function Field({ label, value, onChange, type='text', placeholder, options, required }) {
  const base = { width:'100%', padding:'8px 10px', background:D.s2, border:`1px solid ${D.b1}`, borderRadius:'6px', color:D.tx, fontSize:'13px', fontFamily:"'DM Sans', sans-serif", outline:'none', boxSizing:'border-box' };
  return (
    <div style={{ marginBottom:'12px' }}>
      <label style={{ fontSize:'11px', color:D.tx3, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px', display:'block' }}>{label}{required && ' *'}</label>
      {options ? (
        <select value={value || ''} onChange={e => onChange(e.target.value)} style={{ ...base, cursor:'pointer' }}>
          <option value="">— choisir —</option>
          {options.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : type === 'textarea' ? (
        <textarea value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={{ ...base, minHeight:'60px', resize:'vertical' }} />
      ) : (
        <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder} style={base} />
      )}
    </div>
  );
}

export function Btn({ children, onClick, variant='primary', disabled, style: sx }) {
  const styles = {
    primary: { background: D.or, color: '#09090E', fontWeight: 600 },
    danger:  { background: D.red, color: '#fff', fontWeight: 600 },
    ghost:   { background: 'transparent', color: D.tx3, border: `1px solid ${D.b1}` },
  };
  return (
    <button disabled={disabled} onClick={onClick} style={{
      padding:'7px 16px', borderRadius:'6px', fontSize:'12px', border:'none', cursor: disabled ? 'not-allowed' : 'pointer',
      fontFamily:"'DM Sans', sans-serif", opacity: disabled ? 0.5 : 1, ...styles[variant], ...sx,
    }}>{children}</button>
  );
}

export function Filters({ workspaces, workspaceId, setWorkspaceId, showInactive, setShowInactive }) {
  return (
    <div style={{ display:'flex', gap:'12px', alignItems:'center', marginBottom:'16px', flexWrap:'wrap' }}>
      <div style={{ display:'flex', alignItems:'center', gap:'6px' }}>
        <span style={{ fontSize:'11px', color:D.tx3 }}>Workspace :</span>
        <select value={workspaceId || ''} onChange={e => setWorkspaceId(e.target.value || null)} style={{ padding:'5px 8px', background:D.s2, border:`1px solid ${D.b1}`, borderRadius:'5px', color:D.tx, fontSize:'12px', fontFamily:"'DM Sans', sans-serif" }}>
          <option value="">Global Lexback</option>
          {workspaces.map(ws => <option key={ws.id} value={ws.id}>{ws.name}</option>)}
        </select>
      </div>
      <label style={{ display:'flex', alignItems:'center', gap:'5px', fontSize:'12px', color:D.tx2, cursor:'pointer' }}>
        <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
        Inclure désactivés
      </label>
    </div>
  );
}

export function SourceBadge({ item }) {
  const isGlobal = !item.workspace_id;
  return (
    <span style={{
      fontSize:'9px', fontWeight:500, padding:'2px 6px', borderRadius:'4px',
      background: isGlobal ? `${D.blue}18` : `${D.green}18`,
      color: isGlobal ? D.blue : D.green,
    }}>{isGlobal ? 'Global' : 'Workspace'}</span>
  );
}

export function ActiveBadge({ active }) {
  return active ? null : (
    <span style={{ fontSize:'9px', fontWeight:500, padding:'2px 6px', borderRadius:'4px', background:`${D.red}18`, color:D.red }}>Désactivé</span>
  );
}

export function DataTable({ columns, data, onEdit, onToggle }) {
  const thStyle = { padding:'8px 12px', textAlign:'left', fontSize:'10px', textTransform:'uppercase', letterSpacing:'0.06em', color:D.tx3, borderBottom:`1px solid ${D.b1}`, fontWeight:500 };
  const tdStyle = { padding:'8px 12px', fontSize:'13px', color:D.tx, borderBottom:`1px solid ${D.b1}22` };

  return (
    <div style={{ ...card, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead>
          <tr>
            {columns.map(c => <th key={c.key} style={thStyle}>{c.label}</th>)}
            <th style={{ ...thStyle, textAlign:'right' }}>Actions</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 && (
            <tr><td colSpan={columns.length + 1} style={{ ...tdStyle, textAlign:'center', color:D.tx3, padding:'24px' }}>Aucun élément</td></tr>
          )}
          {data.map(item => (
            <tr key={item.id} style={{ cursor:'pointer' }} onMouseEnter={e => e.currentTarget.style.background='rgba(212,168,67,0.04)'} onMouseLeave={e => e.currentTarget.style.background='transparent'}>
              {columns.map(c => <td key={c.key} style={tdStyle}>{c.render ? c.render(item) : item[c.key]}</td>)}
              <td style={{ ...tdStyle, textAlign:'right' }}>
                <div style={{ display:'flex', gap:'6px', justifyContent:'flex-end' }}>
                  <Btn variant="ghost" onClick={() => onEdit(item)} style={{ padding:'4px 10px' }}>Éditer</Btn>
                  <Btn variant={item.is_active ? 'danger' : 'primary'} onClick={() => onToggle(item)} style={{ padding:'4px 10px', fontSize:'11px' }}>
                    {item.is_active ? 'Désactiver' : 'Réactiver'}
                  </Btn>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: Statuts
// ═══════════════════════════════════════════════════════════════════════════
export function TabStatuts({ workspaces }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { only_active: !showInactive };
    if (workspaceId) params.workspace_id = workspaceId;
    api.get('/api/admin/catalog/status-labels', { params }).then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [workspaceId, showInactive]);

  useEffect(() => { load(); }, [load]);

  const save = async (data, id) => {
    if (id) await api.patch(`/api/admin/catalog/status-labels/${id}`, data);
    else await api.post('/api/admin/catalog/status-labels', { ...data, workspace_id: workspaceId || null });
    setModal(null);
    load();
  };

  const toggle = async (item) => {
    if (item.is_active) await api.delete(`/api/admin/catalog/status-labels/${item.id}`);
    else await api.patch(`/api/admin/catalog/status-labels/${item.id}`, { is_active: true });
    load();
  };

  const columns = [
    { key: 'emoji', label: 'Emoji', render: i => i.emoji || '—' },
    { key: 'name', label: 'Nom' },
    { key: 'color', label: 'Couleur', render: i => i.color ? <span style={{ display:'inline-flex', alignItems:'center', gap:'4px' }}><span style={{ width:'12px', height:'12px', borderRadius:'3px', background:i.color, display:'inline-block', border:`1px solid ${D.b1}` }} />{i.color}</span> : '—' },
    { key: 'internal_bucket', label: 'Bucket' },
    { key: 'position', label: 'Pos.' },
    { key: 'source', label: 'Source', render: i => <><SourceBadge item={i} /> <ActiveBadge active={i.is_active} /></> },
  ];

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
        <Filters workspaces={workspaces} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} showInactive={showInactive} setShowInactive={setShowInactive} />
        <Btn onClick={() => setModal('add')}>+ Ajouter</Btn>
      </div>
      {loading ? <div style={{ color:D.tx3, padding:'24px', textAlign:'center' }}>Chargement…</div> : (
        <DataTable columns={columns} data={items} onEdit={setModal} onToggle={toggle} />
      )}
      {modal && <StatusModal item={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

function StatusModal({ item, onClose, onSave }) {
  const [name, setName] = useState(item?.name || '');
  const [emoji, setEmoji] = useState(item?.emoji || '');
  const [color, setColor] = useState(item?.color || '');
  const [bucket, setBucket] = useState(item?.internal_bucket || '');
  const [position, setPosition] = useState(item?.position ?? 0);
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try {
      await onSave({ name, emoji: emoji || null, color: color || null, internal_bucket: bucket, position: Number(position) }, item?.id);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  return (
    <Modal title={item ? 'Éditer statut' : 'Nouveau statut'} onClose={onClose}>
      <Field label="Nom" value={name} onChange={setName} required />
      <Field label="Emoji" value={emoji} onChange={setEmoji} placeholder="ex: ✅" />
      <Field label="Couleur (hex)" value={color} onChange={setColor} placeholder="#D4A843" />
      <Field label="Bucket interne" value={bucket} onChange={setBucket} options={STATUS_BUCKETS} required />
      <Field label="Position" value={position} onChange={setPosition} type="number" />
      <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'16px' }}>
        <Btn variant="ghost" onClick={onClose}>Annuler</Btn>
        <Btn onClick={handle} disabled={!name || !bucket || saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: Types
// ═══════════════════════════════════════════════════════════════════════════
export function TabTypes({ workspaces }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { only_active: !showInactive };
    if (workspaceId) params.workspace_id = workspaceId;
    api.get('/api/admin/catalog/formality-types', { params }).then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [workspaceId, showInactive]);

  useEffect(() => { load(); }, [load]);

  const save = async (data, id) => {
    if (id) await api.patch(`/api/admin/catalog/formality-types/${id}`, data);
    else await api.post('/api/admin/catalog/formality-types', { ...data, workspace_id: workspaceId || null });
    setModal(null);
    load();
  };

  const toggle = async (item) => {
    if (item.is_active) await api.delete(`/api/admin/catalog/formality-types/${item.id}`);
    else await api.patch(`/api/admin/catalog/formality-types/${item.id}`, { is_active: true });
    load();
  };

  const columns = [
    { key: 'name', label: 'Nom' },
    { key: 'internal_bucket', label: 'Bucket' },
    { key: 'position', label: 'Pos.' },
    { key: 'source', label: 'Source', render: i => <><SourceBadge item={i} /> <ActiveBadge active={i.is_active} /></> },
  ];

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
        <Filters workspaces={workspaces} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} showInactive={showInactive} setShowInactive={setShowInactive} />
        <Btn onClick={() => setModal('add')}>+ Ajouter</Btn>
      </div>
      {loading ? <div style={{ color:D.tx3, padding:'24px', textAlign:'center' }}>Chargement…</div> : (
        <DataTable columns={columns} data={items} onEdit={setModal} onToggle={toggle} />
      )}
      {modal && <TypeModal item={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

function TypeModal({ item, onClose, onSave }) {
  const [name, setName] = useState(item?.name || '');
  const [bucket, setBucket] = useState(item?.internal_bucket || '');
  const [position, setPosition] = useState(item?.position ?? 0);
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try {
      await onSave({ name, internal_bucket: bucket, position: Number(position) }, item?.id);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  return (
    <Modal title={item ? 'Éditer type' : 'Nouveau type'} onClose={onClose}>
      <Field label="Nom" value={name} onChange={setName} required />
      <Field label="Bucket interne" value={bucket} onChange={setBucket} options={TYPE_BUCKETS} required />
      <Field label="Position" value={position} onChange={setPosition} type="number" />
      <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'16px' }}>
        <Btn variant="ghost" onClick={onClose}>Annuler</Btn>
        <Btn onClick={handle} disabled={!name || !bucket || saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: Sub-types
// ═══════════════════════════════════════════════════════════════════════════
export function TabSubTypes({ workspaces }) {
  const [typesWithSubs, setTypesWithSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { only_active: !showInactive };
    if (workspaceId) params.workspace_id = workspaceId;
    api.get('/api/admin/catalog/formality-types-with-subtypes', { params }).then(r => setTypesWithSubs(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [workspaceId, showInactive]);

  useEffect(() => { load(); }, [load]);

  const save = async (data, id) => {
    if (id) await api.patch(`/api/admin/catalog/formality-sub-types/${id}`, data);
    else await api.post('/api/admin/catalog/formality-sub-types', { ...data, workspace_id: workspaceId || null });
    setModal(null);
    load();
  };

  const toggle = async (item) => {
    if (item.is_active) await api.delete(`/api/admin/catalog/formality-sub-types/${item.id}`);
    else await api.patch(`/api/admin/catalog/formality-sub-types/${item.id}`, { is_active: true });
    load();
  };

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
        <Filters workspaces={workspaces} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} showInactive={showInactive} setShowInactive={setShowInactive} />
        <Btn onClick={() => setModal({ _new: true })}>+ Ajouter</Btn>
      </div>
      {loading ? <div style={{ color:D.tx3, padding:'24px', textAlign:'center' }}>Chargement…</div> : (
        typesWithSubs.map(type => (
          <div key={type.id} style={{ marginBottom:'16px' }}>
            <div style={{ fontSize:'12px', fontWeight:600, color:D.or, marginBottom:'6px', textTransform:'uppercase', letterSpacing:'0.06em' }}>
              {type.name}
            </div>
            <DataTable
              columns={[
                { key: 'name', label: 'Nom' },
                { key: 'position', label: 'Pos.' },
                { key: 'source', label: 'Source', render: i => <><SourceBadge item={i} /> <ActiveBadge active={i.is_active} /></> },
              ]}
              data={type.sub_types || []}
              onEdit={setModal}
              onToggle={toggle}
            />
          </div>
        ))
      )}
      {modal && (
        <SubTypeModal
          item={modal._new ? null : modal}
          types={typesWithSubs}
          onClose={() => setModal(null)}
          onSave={save}
        />
      )}
    </div>
  );
}

function SubTypeModal({ item, types, onClose, onSave }) {
  const [name, setName] = useState(item?.name || '');
  const [typeId, setTypeId] = useState(item?.formality_type_id || '');
  const [position, setPosition] = useState(item?.position ?? 0);
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try {
      await onSave({ name, formality_type_id: typeId, position: Number(position) }, item?.id);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  return (
    <Modal title={item ? 'Éditer sous-type' : 'Nouveau sous-type'} onClose={onClose}>
      <Field label="Nom" value={name} onChange={setName} required />
      <div style={{ marginBottom:'12px' }}>
        <label style={{ fontSize:'11px', color:D.tx3, textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:'4px', display:'block' }}>Type parent *</label>
        <select value={typeId} onChange={e => setTypeId(e.target.value)} style={{ width:'100%', padding:'8px 10px', background:D.s2, border:`1px solid ${D.b1}`, borderRadius:'6px', color:D.tx, fontSize:'13px', fontFamily:"'DM Sans', sans-serif" }}>
          <option value="">— choisir —</option>
          {types.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
        </select>
      </div>
      <Field label="Position" value={position} onChange={setPosition} type="number" />
      <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'16px' }}>
        <Btn variant="ghost" onClick={onClose}>Annuler</Btn>
        <Btn onClick={handle} disabled={!name || !typeId || saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: Greffes
// ═══════════════════════════════════════════════════════════════════════════
export function TabGreffes({ workspaces }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { only_active: !showInactive };
    if (workspaceId) params.workspace_id = workspaceId;
    api.get('/api/admin/catalog/greffes', { params }).then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [workspaceId, showInactive]);

  useEffect(() => { load(); }, [load]);

  const save = async (data, id) => {
    if (id) await api.patch(`/api/admin/catalog/greffes/${id}`, data);
    else await api.post('/api/admin/catalog/greffes', { ...data, workspace_id: workspaceId || null });
    setModal(null);
    load();
  };

  const toggle = async (item) => {
    if (item.is_active) await api.delete(`/api/admin/catalog/greffes/${item.id}`);
    else await api.patch(`/api/admin/catalog/greffes/${item.id}`, { is_active: true });
    load();
  };

  const columns = [
    { key: 'name', label: 'Nom' },
    { key: 'code_postal', label: 'CP', render: i => i.code_postal || '—' },
    { key: 'ville', label: 'Ville', render: i => i.ville || '—' },
    { key: 'source', label: 'Source', render: i => <><SourceBadge item={i} /> <ActiveBadge active={i.is_active} /></> },
  ];

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
        <Filters workspaces={workspaces} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} showInactive={showInactive} setShowInactive={setShowInactive} />
        <Btn onClick={() => setModal('add')}>+ Ajouter</Btn>
      </div>
      {loading ? <div style={{ color:D.tx3, padding:'24px', textAlign:'center' }}>Chargement…</div> : (
        <DataTable columns={columns} data={items} onEdit={setModal} onToggle={toggle} />
      )}
      {modal && <GreffeModal item={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

function GreffeModal({ item, onClose, onSave }) {
  const [name, setName] = useState(item?.name || '');
  const [cp, setCp] = useState(item?.code_postal || '');
  const [ville, setVille] = useState(item?.ville || '');
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try {
      await onSave({ name, code_postal: cp || null, ville: ville || null }, item?.id);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  return (
    <Modal title={item ? 'Éditer greffe' : 'Nouveau greffe'} onClose={onClose}>
      <Field label="Nom" value={name} onChange={setName} required />
      <Field label="Code postal" value={cp} onChange={setCp} placeholder="75001" />
      <Field label="Ville" value={ville} onChange={setVille} placeholder="Paris" />
      <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'16px' }}>
        <Btn variant="ghost" onClick={onClose}>Annuler</Btn>
        <Btn onClick={handle} disabled={!name || saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// TAB: Cabinets
// ═══════════════════════════════════════════════════════════════════════════
export function TabCabinets({ workspaces }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  const [modal, setModal] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const params = { only_active: !showInactive };
    if (workspaceId) params.workspace_id = workspaceId;
    api.get('/api/admin/catalog/cabinets', { params }).then(r => setItems(r.data || [])).catch(() => {}).finally(() => setLoading(false));
  }, [workspaceId, showInactive]);

  useEffect(() => { load(); }, [load]);

  const save = async (data, id) => {
    if (id) await api.patch(`/api/admin/catalog/cabinets/${id}`, data);
    else await api.post('/api/admin/catalog/cabinets', { ...data, workspace_id: workspaceId || null });
    setModal(null);
    load();
  };

  const toggle = async (item) => {
    if (item.is_active) await api.delete(`/api/admin/catalog/cabinets/${item.id}`);
    else await api.patch(`/api/admin/catalog/cabinets/${item.id}`, { is_active: true });
    load();
  };

  const columns = [
    { key: 'name', label: 'Nom' },
    { key: 'email_contact', label: 'Email', render: i => i.email_contact || '—' },
    { key: 'siren', label: 'SIREN', render: i => i.siren || '—' },
    { key: 'telephone', label: 'Tél.', render: i => i.telephone || '—' },
    { key: 'source', label: 'Source', render: i => <><SourceBadge item={i} /> <ActiveBadge active={i.is_active} /></> },
  ];

  return (
    <div>
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'12px' }}>
        <Filters workspaces={workspaces} workspaceId={workspaceId} setWorkspaceId={setWorkspaceId} showInactive={showInactive} setShowInactive={setShowInactive} />
        <Btn onClick={() => setModal('add')}>+ Ajouter</Btn>
      </div>
      {loading ? <div style={{ color:D.tx3, padding:'24px', textAlign:'center' }}>Chargement…</div> : (
        <DataTable columns={columns} data={items} onEdit={setModal} onToggle={toggle} />
      )}
      {modal && <CabinetModal item={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

function CabinetModal({ item, onClose, onSave }) {
  const [name, setName] = useState(item?.name || '');
  const [email, setEmail] = useState(item?.email_contact || '');
  const [siren, setSiren] = useState(item?.siren || '');
  const [tel, setTel] = useState(item?.telephone || '');
  const [notes, setNotes] = useState(item?.notes || '');
  const [saving, setSaving] = useState(false);

  const handle = async () => {
    setSaving(true);
    try {
      await onSave({ name, email_contact: email || null, siren: siren || null, telephone: tel || null, notes: notes || null }, item?.id);
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    setSaving(false);
  };

  return (
    <Modal title={item ? 'Éditer cabinet' : 'Nouveau cabinet'} onClose={onClose}>
      <Field label="Nom" value={name} onChange={setName} required />
      <Field label="Email" value={email} onChange={setEmail} type="email" placeholder="contact@cabinet.fr" />
      <Field label="SIREN (9 chiffres)" value={siren} onChange={setSiren} placeholder="123456789" />
      <Field label="Téléphone" value={tel} onChange={setTel} placeholder="01 23 45 67 89" />
      <Field label="Notes" value={notes} onChange={setNotes} type="textarea" />
      <div style={{ display:'flex', gap:'8px', justifyContent:'flex-end', marginTop:'16px' }}>
        <Btn variant="ghost" onClick={onClose}>Annuler</Btn>
        <Btn onClick={handle} disabled={!name || saving}>{saving ? 'Enregistrement…' : 'Enregistrer'}</Btn>
      </div>
    </Modal>
  );
}

// ═══════════════════════════════════════════════════════════════════════════
// CATALOGUE CONTENT — composant complet sans layout (utilisé dans Paramètres)
// ═══════════════════════════════════════════════════════════════════════════
export function CatalogueContent({ withHeader = true }) {
  const [activeTab, setActiveTab] = useState('statuts');
  const [workspaces, setWorkspaces] = useState([]);

  useEffect(() => {
    api.get('/api/admin/workspaces').then(r => setWorkspaces(r.data || [])).catch(() => {});
  }, []);

  return (
    <div>
      {withHeader && (
        <div style={{ marginBottom:'16px' }}>
          <h2 style={{ fontFamily:'Syne, sans-serif', fontSize:'16px', fontWeight:600, color:D.tx, margin:0 }}>
            Catalogue extensible
          </h2>
          <p style={{ fontSize:'11px', color:D.tx3, margin:'4px 0 0' }}>
            Statuts, types de formalité, sous-types, greffes, cabinets.
          </p>
        </div>
      )}

      {/* Onglets horizontaux */}
      <div style={{ display:'flex', gap:'2px', borderBottom:`1px solid ${D.b1}`, marginBottom:'16px' }}>
        {TABS.map(tab => (
          <div key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            padding:'8px 14px', cursor:'pointer', fontSize:'12px',
            color: activeTab === tab.id ? D.or : D.tx2,
            borderBottom: activeTab === tab.id ? `2px solid ${D.or}` : '2px solid transparent',
            marginBottom:'-1px',
            transition:'all 0.12s',
            fontWeight: activeTab === tab.id ? 600 : 400,
          }}>
            {tab.label}
          </div>
        ))}
      </div>

      {activeTab === 'statuts'    && <TabStatuts workspaces={workspaces} />}
      {activeTab === 'types'      && <TabTypes workspaces={workspaces} />}
      {activeTab === 'sous-types' && <TabSubTypes workspaces={workspaces} />}
      {activeTab === 'greffes'    && <TabGreffes workspaces={workspaces} />}
      {activeTab === 'cabinets'   && <TabCabinets workspaces={workspaces} />}
    </div>
  );
}
